import { IconSearch } from '@developerz/ui';
import { batch, createSignal, createUniqueId, type JSX } from 'solid-js';
import { t } from '../i18n';
import { type AdminUserStatus, type AdminUserRow as UserRow, useAdminUsers } from '../lib/admin';
import { usePagedQuery } from '../lib/paged';
import { AdminInviteByEmail } from './AdminInviteByEmail';
import { AdminUsersTable } from './AdminUsersTable';
import { EmptyState } from './EmptyState';
import { LoadMore } from './LoadMore';
import { PageHeader } from './PageHeader';
import { Panel } from './Panel';
import { QueryBoundary } from './QueryBoundary';
import { Select } from './Select';
import { Skeleton } from './Skeleton';
import { Button } from './ui/Button';

// Admin user registry container. A status filter (all / approved / waitlisted)
// plus a `q` search, cursor-paginated (`GET /v1/admin/users`). Approve / suspend
// happen per-row via the `user` MCP verb. Mounts only for staff (behind
// <AdminGate/>). Paging lives in `usePagedQuery`; this owns the two filters.

const STATUSES: readonly AdminUserStatus[] = ['all', 'approved', 'waitlisted'];

export function AdminUsersPanel(props: { token: string | null }): JSX.Element {
  const [status, setStatus] = createSignal<AdminUserStatus>('all');
  const statusId = createUniqueId();
  const [draft, setDraft] = createSignal('');
  const [q, setQ] = createSignal('');

  const list = usePagedQuery<UserRow>((cursor) =>
    useAdminUsers(status, q, cursor, 30, props.token),
  );

  // Each filter change is ONE batched write with the reset, so the query key
  // jumps straight to the new filter at `cursor=null` rather than firing a
  // throwaway fetch at the cursor the previous filter had reached.
  const filter = (next: AdminUserStatus): void =>
    batch(() => {
      setStatus(next);
      list.reset();
    });

  // A re-search for the SAME text stays a no-op: with no key change to refill
  // them, resetting would just empty the rows already on screen.
  const search = (): void => {
    const next = draft().trim();
    if (next === q()) return;
    batch(() => {
      setQ(next);
      list.reset();
    });
  };

  return (
    <>
      {/* Invite by email (plan 102 slice 05): a friend gets their own funded
          account; a landed invite rebases the registry like an approve. */}
      <PageHeader
        title={t('admin.users_title')}
        desc={t('admin.users_subtitle')}
        actions={<AdminInviteByEmail token={props.token} onInvited={() => list.reload()} />}
      />

      {/* A semantic <search> around the whole filter row (status, query, button),
          as Orgs has it — NOT a <form>: an async list <For/> beside a real
          <form> trips a happy-dom template-walk crash. Search fires on the
          button or Enter. */}
      <search class="filter-row">
        <div class="admin-filter__select">
          <label id={`${statusId}-label`} for={statusId} class="sr-only">
            {t('admin.users_status_label')}
          </label>
          <Select
            a11y={{ id: statusId }}
            label={t('admin.users_status_label')}
            value={status()}
            options={STATUSES.map((s) => ({ value: s, label: t(`admin.status_${s}`) }))}
            onChange={filter}
          />
        </div>
        <div class="search-input">
          <IconSearch size="1rem" />
          <input
            id="admin-users-q"
            type="search"
            class="input"
            placeholder={t('admin.users_search_label')}
            aria-label={t('admin.users_search_label')}
            value={draft()}
            onInput={(e) => setDraft(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') search();
            }}
          />
        </div>
        <Button variant="outline" onClick={search}>
          {t('admin.search')}
        </Button>
      </search>

      <Panel title={t('admin.users_all')}>
        <QueryBoundary
          query={list.view}
          isEmpty={(r) => r.length === 0}
          loading={
            <div class="admin__skeletons">
              <Skeleton height="3rem" />
              <Skeleton height="3rem" />
            </div>
          }
          empty={<EmptyState message={t('admin.users_empty')} />}
        >
          {(r) => (
            <>
              {/* `reload()`, not `refetch()`: an approve/suspend re-slices the
                  registry server-side, so refreshing only the cursor on screen
                  leaves the deeper pages computed against an offset that moved
                  — rows duplicate or vanish. `reload()` rebases from page one. */}
              <AdminUsersTable rows={r} token={props.token} onMutated={list.reload} />
              <LoadMore list={list} label={t('admin.load_more')} />
            </>
          )}
        </QueryBoundary>
      </Panel>
    </>
  );
}
